import { Combobox, type ComboboxOption } from "@/shared/ui/Combobox";
import type { ProductOption } from "../types";

// Searchable catalogue picker — thin adapter over the shared <Combobox> (Radix Popover + cmdk).
// The previous hand-rolled portal/positioning/outside-click implementation is gone; the primitive
// now handles overflow-escaping (Popover portals to body), keyboard nav and focus return for free.
//
// External API is unchanged so call sites (AgreementStep row + AgreementEditDrawer) need no edits:
// `value` is the selected canonical "Brand Model" name; `onChange` still receives the full option.
export function ProductSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  isLoading,
  dense = false,
}: {
  options: ProductOption[];
  value: string; // currently selected canonical name
  onChange: (opt: ProductOption) => void;
  placeholder: string;
  searchPlaceholder: string;
  isLoading: boolean;
  // `dense` = compact table-cell sizing (matches EditCell: px-2 py-1 text-[12px]); default is the
  // comfortable form control (px-3.5 py-2.5 text-[14px]) used in the edit drawer.
  dense?: boolean;
}) {
  // Combobox keys on a string id; map ProductOption.id (number) → string and brand → sublabel.
  const comboOptions: ComboboxOption[] = options.map((o) => ({
    id: String(o.id),
    label: o.name,
    sublabel: o.brand ?? undefined,
  }));
  // The incoming `value` is a canonical name (it may be a typed name not present in `options`).
  const selected = options.find((o) => o.name === value);

  return (
    <Combobox
      options={comboOptions}
      value={selected ? String(selected.id) : ""}
      onSelect={(id) => {
        const opt = options.find((o) => String(o.id) === id);
        if (opt) onChange(opt);
      }}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      isLoading={isLoading}
      dense={dense}
      // Show the canonical name even when it doesn't resolve to a catalogue option (typed value).
      triggerLabel={value || undefined}
    />
  );
}
